import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { Check, ChevronRight } from "lucide-react";
import React from "react";

export const ContextMenuRoot = ContextMenuPrimitive.Root;
export const ContextMenuTrigger = ContextMenuPrimitive.Trigger;
export const ContextMenuPortal = ContextMenuPrimitive.Portal;
export const ContextMenuSub = ContextMenuPrimitive.Sub;
export const ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup;

export const ContextMenuSeparator = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <ContextMenuPrimitive.Separator
        ref={ref}
        className={cn("my-1 h-px bg-border-base bg-content-tertiary/20", className)}
        {...props}
    />
));
ContextMenuSeparator.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;

export const ContextMenuContent = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <ContextMenuPrimitive.Content
        ref={ref}
        className={cn(
            "z-50", // z-index is consistent with our dropdown menus
            "min-w-[220px] rounded-md bg-surface-01 p-1 shadow-lg",
            "w-64 rounded-lg border border-border-base bg-surface-popover p-0 shadow first:pt-1 last:pb-1",
            className,
        )}
        {...props}
    />
));
ContextMenuContent.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;

import { cva } from "class-variance-authority";
import type { BaseIconProps } from "@/assets/icons/utils";

const contextMenuItemVariants = cva(
    cn(
        "flex cursor-pointer items-center outline-none rounded text-base text-content-primary px-2 py-2 h-8",
        "hover:bg-surface-hover focus:bg-surface-hover",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    ),
    {
        variants: {
            variant: {
                default: "",
                destructive: "text-content-destructive",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

export const ContextMenuItem = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.ContextMenuItem>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.ContextMenuItem> &
        PropsWithClassName & { variant?: "default" | "destructive"; LeadingIcon?: React.ComponentType<BaseIconProps> }
>(({ onClick, className, variant, LeadingIcon, children, ...props }, ref) => (
    <ContextMenuPrimitive.ContextMenuItem
        ref={ref}
        className={cn(contextMenuItemVariants({ variant }), className)}
        onClick={(e) => {
            if (onClick) {
                e.stopPropagation();
                // We're using setTimeout to allow the context menu to clear its modification to body.style before triggering the onclick,
                // This is important if the onClick handler opens a modal, as otherwise the modal will reset the style when the modal is
                // closed, which causes the pointer-events:none to stick around.
                setTimeout(() => onClick(e), 0);
            }
        }}
        {...props}
    >
        {LeadingIcon ? (
            <span className="mr-2">
                <LeadingIcon size="sm" />
            </span>
        ) : null}
        {children}
    </ContextMenuPrimitive.ContextMenuItem>
));
ContextMenuItem.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;

export const ContextMenuSubTrigger = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.SubTrigger>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubTrigger> & PropsWithClassName & { inset?: boolean }
>(({ className, inset, children, ...props }, ref) => (
    <ContextMenuPrimitive.SubTrigger
        ref={ref}
        className={cn(
            contextMenuItemVariants({ variant: "default" }),
            "data-[state=open]:bg-surface-hover",
            inset && "pl-8",
            className,
        )}
        {...props}
    >
        {children}
        <ChevronRight className="ml-auto size-4" />
    </ContextMenuPrimitive.SubTrigger>
));
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName;

export const ContextMenuSubContent = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.SubContent>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubContent> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <ContextMenuPrimitive.SubContent
        ref={ref}
        className={cn(
            "z-50 min-w-[8rem] overflow-hidden rounded-lg border border-border-base bg-surface-popover p-0 py-1 shadow",
            "outline-none focus:outline-none focus-visible:ring-0",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
        )}
        {...props}
    />
));
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName;

export const ContextMenuRadioItem = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.RadioItem>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.RadioItem> & PropsWithClassName
>(({ className, children, ...props }, ref) => (
    <ContextMenuPrimitive.RadioItem
        ref={ref}
        className={cn(contextMenuItemVariants({ variant: "default" }), "pl-8", className)}
        {...props}
    >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <ContextMenuPrimitive.ItemIndicator>
                <Check className="size-4" />
            </ContextMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </ContextMenuPrimitive.RadioItem>
));
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName;
